/**
 * Hook for managing WebSocket connection to SAAGA Agents API.
 */

import { useMemo, useState } from 'react'

import { useAppDispatch } from '../store/store'
import type {
  UseAgentsWebsocketReturn,
  WebSocketConnectionStatus,
} from './agents-websocket.interface'
import { useWebsocketActions } from './use-websocket-actions.hook'
import { useConnectionCheck } from './use-websocket-connection.hook'

export type {
  UseAgentsWebsocketReturn,
  WebSocketConnectionStatus,
  WebSocketConnectParams,
} from './agents-websocket.interface'

export const useAgentsWebsocket = (): UseAgentsWebsocketReturn => {
  const dispatch = useAppDispatch()
  const [connectionStatus, setConnectionStatus] =
    useState<WebSocketConnectionStatus>('disconnected')
  const [connectionError, setConnectionError] = useState<string | null>(null)

  const { startCheck, clearCheck } = useConnectionCheck({
    setConnectionStatus,
  })

  const { connect, disconnect, sendMessage } = useWebsocketActions({
    dispatch,
    setConnectionStatus,
    setConnectionError,
    startCheck,
    clearCheck,
  })

  return useMemo(
    () => ({
      connectionStatus,
      isConnected: connectionStatus === 'connected',
      isConnecting: connectionStatus === 'connecting',
      connectionError,
      connect,
      disconnect,
      sendMessage,
    }),
    [connectionStatus, connectionError, connect, disconnect, sendMessage]
  )
}
